import { Utils } from 'core/utils';
import { ControllerOptions, normalizedOptions, TextApplianceResult,
         TextController, TextControllerBase, TextEditingResult
       } from './text.controller';
import { TextControllerMethods, TextEditingRefusalReason
       } from './text.controller.methods';
import { FontDescription, Text, TextChunk } from './text.model';

export const ZoomModeLimit: TextController = {
  setColor: TextControllerBase.setColor,
  setHorizontalAlignment: TextControllerBase.setHorizontalAlignment,
  setVerticalAlignment: TextControllerBase.setVerticalAlignment,
  setAvailableArea: TextControllerBase.setAvailableArea,

  setText: (
    model: Text,
    newText: Array<TextChunk>,
    _?: ControllerOptions
  ): TextEditingResult =>
  {
    let workingModel = Utils.deepCopy(model);
    newText = TextControllerMethods.removeVariationSelectors(newText);

    for (let chunkIdx = 0; chunkIdx < newText.length; chunkIdx++) {
      const chunk = newText[chunkIdx];
      if (!TextControllerMethods.textIsCoveredByUnicodeRanges(
          chunk.content, model.fontDescription.unicodeRanges)) {
        return <TextEditingResult> {
          type: TextApplianceResult.Failure,
          reason: TextEditingRefusalReason.CharacterNotSupported,
          model: model,
          position: {
            chunk: chunkIdx,
            character: -1
          }
        };
      }
    }

    workingModel.text = newText;
    const reason =
        TextControllerMethods.getUsageAvailableAreaComparison(workingModel);

    if (reason !== TextEditingRefusalReason.None) {
      return <TextEditingResult> {
        type: TextApplianceResult.Failure,
        reason: reason,
        model: model,
      };
    }
    else {
      return <TextEditingResult>  {
        type: TextApplianceResult.Success,
        model: workingModel
      };
    }
  },

  setFont: (
    model: Text,
    fontDescription: FontDescription,
    _?: ControllerOptions
  ): TextEditingResult =>
  {
    let workingModel = Utils.deepCopy(model);
    workingModel.fontName = fontDescription.name;
    workingModel.fontDescription = fontDescription;
    workingModel.leading = fontDescription.charDimensions.minLeading;

    const unicodeCheckResult =
        TextControllerMethods.checkForInvalidSymbols(workingModel);
    if (unicodeCheckResult !== TextEditingRefusalReason.None) {
      return <TextEditingResult> {
        type: TextApplianceResult.Failure,
        reason: TextEditingRefusalReason.CharacterNotSupported,
        model: model,
        position: unicodeCheckResult
      };
    }

    const reason =
        TextControllerMethods.getUsageAvailableAreaComparison(workingModel);

    if (reason !== TextEditingRefusalReason.None) {
      return <TextEditingResult> {
        type: TextApplianceResult.Failure,
        reason: reason,
        model: model,
      };
    }
    else {
      return <TextEditingResult> {
        type: TextApplianceResult.Success,
        model: workingModel,
      };
    }
  },

  setFontSize: (
    model: Text,
    fontSize: number,
    rawOptions?: ControllerOptions
  ): TextEditingResult =>
  {
    const options = normalizedOptions(rawOptions);
    let workingModel = Utils.deepCopy(model);
    workingModel.fontSize = fontSize;

    if (fontSize > options.maxFontSize) {
      return <TextEditingResult> {
        type: TextApplianceResult.Failure,
        reason: TextEditingRefusalReason.TextTooWide,
        model: model,
      };
    }

    if (fontSize < options.minFontSize) {
      return <TextEditingResult> {
        type: TextApplianceResult.Failure,
        reason: TextEditingRefusalReason.MinFontSizeUndershot,
        model: model,
      };
    }

    const reason =
        TextControllerMethods.getUsageAvailableAreaComparison(workingModel);
    if (reason !== TextEditingRefusalReason.None) {
      return <TextEditingResult> {
        type: TextApplianceResult.Failure,
        reason: reason,
        model: model,
      };
    }

    return <TextEditingResult> {
      type: TextApplianceResult.Success,
      model: workingModel,
    };
  }
};
